<template>
  <v-container fluid :class="$vuetify.breakpoint.smAndDown ? 'py-9' : 'pa-9'">
    <!-- Encabezado -->
    <v-row>
      <v-col cols="12">
        <h1 class="text-h5 font-weight-bold mb-2">Configuración de Suscripciones</h1>
        <p class="text-body-1 text--secondary">
          Gestiona la funcionalidad de suscripciones para tus membresías
        </p>
      </v-col>
    </v-row>

    <v-divider class="my-6"></v-divider>

    <!-- Panel de Activación -->
    <v-row>
      <v-col cols="12">
        <activation-panel
          v-model="subscriptionsEnabled"
          :loading="loading"
          :activating="activating"
          @input="onToggleSubscriptions"
        />
      </v-col>
    </v-row>

    <!-- Configuración de Suscripciones (visible solo cuando está activo) -->
    <v-expand-transition>
      <div v-if="subscriptionsEnabled" id="config">
        <v-row>
          <v-col cols="12">
            <configuration-card
              :config="config"
              :saving="saving"
              :auto-cancel-options="autoCancelOptions"
              @save="saveConfiguration"
              @reset="resetConfiguration"
            />
          </v-col>
        </v-row>
      </div>
    </v-expand-transition>

    <!-- Dialog de Confirmación -->
    <confirmation-dialog
      v-model="showConfirmDialog"
      :activating="activating"
      :features="features"
      @confirm="confirmActivation"
      @cancel="cancelActivation"
    />

    <!-- Snackbar para notificaciones -->
    <v-snackbar
      v-model="snackbar"
      :color="snackbarColor"
      :timeout="3000"
      top
    >
      {{ snackbarText }}
      <template v-slot:action="{ attrs }">
        <v-btn
          text
          v-bind="attrs"
          @click="snackbar = false"
        >
          Cerrar
        </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import ActivationPanel from '@/components/dashboard/subscription/ActivationPanel.vue';
import ConfigurationCard from '@/components/dashboard/subscription/ConfigurationCard.vue';
import ConfirmationDialog from '@/components/dashboard/subscription/ConfirmationDialog.vue';
import { mapState } from 'vuex';

export default {
  name: 'SubscriptionConfig',
  
  components: {
    ActivationPanel,
    ConfigurationCard,
    ConfirmationDialog
  },

  data() {
    return {
      subscriptionsEnabled: false,
      showConfirmDialog: false,
      loading: false,
      saving: false,
      activating: false,
      
      // Configuración
      config: {
        paymentRetries: 3,
        gracePeriodDays: 7,
        autoCancelEnabled: false,
      },

      // Funcionalidades
      features: [
        {
          icon: 'mdi-autorenew',
          text: 'Cobros automáticos recurrentes según el período de membresía'
        },
        {
          icon: 'mdi-credit-card-refresh',
          text: 'Gestión automática de reintentos de pago'
        },
        {
          icon: 'mdi-email-outline',
          text: 'Notificaciones automáticas a miembros sobre pagos'
        },
        {
          icon: 'mdi-calendar-clock',
          text: 'Período de gracia configurable para pagos fallidos'
        },
        {
          icon: 'mdi-shield-check',
          text: 'Gestión segura de métodos de pago'
        }
      ],

      // Opciones de cancelación automática
      autoCancelOptions: [
        {
          text: 'Desactivada',
          value: false,
          description: 'Las suscripciones no se cancelarán automáticamente'
        },
        {
          text: 'Activada',
          value: true,
          description: 'Las suscripciones se cancelarán automáticamente'
        }
      ],

      // Snackbar
      snackbar: false,
      snackbarText: '',
      snackbarColor: 'success',
    };
  },

  mounted() {
    this.loadConfiguration();
  },

  computed: {
    ...mapState("user", ["user"]),
  },

  methods: {
    async loadConfiguration() {
      this.loading = true;
      try {
        const response = await this.axios({
          method: 'GET',
          url: '/billing/config',
        })

        const billingConfig = response.data.billingConfig;
        console.log('Configuration response:', response);

        if (response && billingConfig) {
          this.config = {
            paymentRetries: billingConfig.paymentRetries || 3,
            gracePeriodDays: billingConfig.gracePeriodDays || 7,
            autoCancelEnabled: billingConfig.automaticCancellation || false,
          };
          this.subscriptionsEnabled = billingConfig.isActive || false;
        }
      } catch (error) {
        console.error('Error loading configuration:', error);
        this.showNotification('Error al cargar la configuración', 'error');
      } finally {
        this.loading = false;
      }
    },

    onToggleSubscriptions(value) {
      if (value) {
        // Mostrar dialog de confirmación al activar
        this.showConfirmDialog = true;
      } else {
        // Desactivar directamente
        this.deactivateSubscriptions();
      }
    },

    async confirmActivation() {
      this.activating = true;
      try {
        await this.axios({
          method: 'PUT',
          url: '/billing/config',
          data: {
            isActive: true
          }
        })
        
        this.showConfirmDialog = false;
        this.showNotification('Suscripciones activadas correctamente', 'success');
      } catch (error) {
        console.error('Error activating subscriptions:', error);
        this.showNotification('Error al activar suscripciones', 'error');
        this.subscriptionsEnabled = false;
      } finally {
        this.activating = false;
      }
    },

    cancelActivation() {
      this.subscriptionsEnabled = false;
      this.showConfirmDialog = false;
    },

    async deactivateSubscriptions() {
      this.loading = true;
      try {
        await this.axios({
          method: 'PUT',
          url: '/billing/config',
          data: {
            isActive: false
          }
        })
        
        this.showNotification('Suscripciones desactivadas', 'info');
      } catch (error) {
        console.error('Error deactivating subscriptions:', error);
        this.showNotification('Error al desactivar suscripciones', 'error');
        this.subscriptionsEnabled = true;
      } finally {
        this.loading = false;
      }
    },

    async saveConfiguration(newConfig) {
      this.saving = true;
      try {
        await this.axios({
          method: 'PUT',
          url: '/billing/config',
          data: newConfig
        })

        this.config = { ...newConfig };
        this.showNotification('Configuración guardada correctamente', 'success');
      } catch (error) {
        console.error('Error saving configuration:', error);
        this.showNotification('Error al guardar la configuración', 'error');
      } finally {
        this.saving = false;
      }
    },

    showNotification(text, color = 'success') {
      this.snackbarText = text;
      this.snackbarColor = color;
      this.snackbar = true;
    },

    resetConfiguration() {
      // Restaurar valores por defecto
      this.config = {
        paymentRetries: 3,
        gracePeriodDays: 7,
        autoCancelEnabled: false,
      };
      this.showNotification('Configuración restablecida a valores por defecto', 'info');
    }
  }
};
</script>

<style scoped>
/* Scroll suave */
html {
  scroll-behavior: smooth;
}
</style>